import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { authClient } from '@/auth';

/** Marks a request already replayed after a token refresh, so a second 401 can't loop. */
const AUTH_REFRESH_RETRY_FLAG = '__authRefreshRetry__';

/** Token-minting routes — a 401 there means bad credentials, not an expired session. */
const AUTH_MINT_PATHS = ['/auth/login', '/auth/refresh'];

function isAuthRefreshRetry(config: AxiosRequestConfig | undefined): boolean {
  return Boolean(config && (config as Record<string, unknown>)[AUTH_REFRESH_RETRY_FLAG]);
}

function isAuthMintRequest(config: AxiosRequestConfig | undefined): boolean {
  const url = config?.url ?? '';
  return AUTH_MINT_PATHS.some((path) => url.includes(path));
}

// Module-level single-flight so a burst of 401s (e.g. several queries firing
// after the tab wakes with an expired token) triggers exactly one refresh,
// independent of whether the installed auth package single-flights internally.
let refreshInFlight: Promise<void> | null = null;

function refreshOnce(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = authClient.refresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Reactive complement to the AuthClient's proactive expiry timer. Whenever any
 * request through the shared axios instance comes back 401 (our signed access
 * token expired or was invalidated — e.g. the timer missed while the tab slept),
 * mint a fresh token from the live Firebase session (`authClient.refresh()`
 * force-refreshes the Firebase ID token and POSTs it to the api) and replay the
 * original request once with the new bearer.
 *
 * If the refresh itself fails, the original 401 propagates — and when the
 * session is truly dead (no Firebase user, or the server rejects it) the
 * AuthClient clears local state so the app falls back to the signed-out UI.
 *
 * Returns a teardown function (useful for tests / HMR).
 */
export function installAuthRefreshInterceptor(instance: AxiosInstance): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config;
      if (error.response?.status !== 401 || !config || isAuthRefreshRetry(config) || isAuthMintRequest(config)) {
        throw error;
      }

      try {
        await refreshOnce();
      } catch {
        throw error; // refresh failed — the original 401 stands
      }

      return instance.request({
        ...config,
        headers: { ...config.headers, ...authClient.getAuthHeaders() },
        [AUTH_REFRESH_RETRY_FLAG]: true,
      } as AxiosRequestConfig);
    },
  );
  return () => instance.interceptors.response.eject(id);
}
